import { Field, Form, Formik } from "formik";
import Button from "pages/_components/Button";
import GridTable from "pages/_components/GridTable/GridTable";
import Notification from "pages/_components/Notification";
import TextField from "pages/_components/fields/TextField";
import { bool } from "prop-types";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as i18n from "util/i18n";
import MainContainer from "pages/_components/MainContainer";
// eslint-disable-next-line import/no-named-as-default
import ResponsiveHeader, { DesktopButtons, MobileButtons } from "pages/_components/header";
import useDebounce from "pages/transfers/hooks/useDebounce";
import { actions as comexActions } from "reducers/comex";
import Image from "pages/_components/Image";
import I18n from "pages/_components/I18n";
import DateField from "pages/_components/fields/DateField";
import Action from "pages/_components/Action";
import { push } from "connected-react-router";
import * as Yup from "yup";
import ComexStepper from "../_components/ComexStepper";

const FORM_ID = "comex.tax";

const StepImportPayment = ({ isDesktop }) => {
    const dispatch = useDispatch();

    const { formRef } = useDebounce();

    const [isSaveEnabled, setIsSaveEnabled] = useState(false);
    const { comexTransfer: comexTransferRequest = {}, comexId = null } = useSelector((state) => state.comex);
    const { dispatchData = {}, selectedConcept = "", otherConcepts = "" } = comexTransferRequest;

    const title = i18n.get("comex.tax.importPayment.title");
    const [isSaved, setIsSaved] = useState(false);
    const isB05orB12 = (otherConcepts && otherConcepts.includes("B05")) || selectedConcept.includes("B12");

    const initialValues = {
        customsEntryDate: dispatchData?.customsEntryDate ?? "",
        grantedBuyCondition: dispatchData?.grantedBuyCondition ?? "",
        agreedPaymentCondition: dispatchData?.agreedPaymentCondition ?? "",
        expirationDate: dispatchData?.expirationDate ?? "",
        arancelPosition: dispatchData?.arancelPosition ?? "",
        deliveryEstimatedTime: dispatchData?.deliveryEstimatedTime ?? "",
    };

    const saveComexRequest = (isContinue) => {
        const _comexTransferRequest = {
            ...comexTransferRequest,
            step: "stepDISPATCH",
            comexId,
            isContinue,
        };
        dispatch(comexActions.createComexTransfer(_comexTransferRequest));
        setIsSaved(true);
    };

    const handleSubmit = () => {
        saveComexRequest(true);
        dispatch(push("/comex/retention"));
    };

    const [formData, setFormData] = useState({ initialValues });

    const isFormComplete =
        formData.customsEntryDate &&
        formData.grantedBuyCondition &&
        formData.agreedPaymentCondition &&
        formData.expirationDate &&
        formData.arancelPosition &&
        (!isB05orB12 || formData.deliveryEstimatedTime);

    useEffect(() => {
        dispatch(
            comexActions.setComexTransfer({
                ...comexTransferRequest,
                dispatchData: {
                    ...formData,
                },
            }),
        );

        setIsSaveEnabled(isFormComplete);
        setIsSaved(false);
    }, [formData, dispatch]);

    const validator = Yup.string()
        .nullable()
        .test(
            "not-only-spaces",
            i18n.get("comex.importpayment.field.error.empty"),
            (value) => !!value && value.trim().length > 0,
        )
        .matches(/^[A-Za-zÁÉÍÓÚáéíóúÑñ0-9 ]+$/, i18n.get("comex.importpayment.field.error.invalid"))
        .required(i18n.get("comex.importpayment.field.error.empty"));

    const validationSchema = Yup.object().shape({
        grantedBuyCondition: validator,
        agreedPaymentCondition: validator,
        arancelPosition: validator,
    });

    const [isValid, setIsValid] = useState(false);

    useEffect(() => {
        const checkValid = async () => {
            try {
                await validationSchema.validate(formData, { abortEarly: false });
                setIsValid(true);
            } catch (e) {
                setIsValid(false);
            }
        };
        checkValid();
    }, [formData]);

    return (
        <>
            {isDesktop && <ComexStepper isDesktop={isDesktop} currentStep={4} />}
            <Notification scopeToShow="ComexNotification" />
            <ResponsiveHeader ticket metaTitle="transfers.comex.title" backLinkTo="/comex/clearance">
                <DesktopButtons>
                    <Button
                        className="btn btn-outline btn-block small"
                        onClick={() => {
                            saveComexRequest();
                        }}
                        disabled={!isSaveEnabled || isSaved}>
                        <Image
                            src={isSaved ? "images/figma/icon/check.svg" : "images/figma/icon/save.svg"}
                            className="icon-medium svg-icon"
                        />
                        <I18n id={isSaved ? "comex.transfer.save.ok.btn" : "comex.transfer.save.btn"} />
                    </Button>
                </DesktopButtons>
                <MobileButtons>
                    <Action
                        labelKey="comex.transfer.save.btn"
                        handleClick={() => saveComexRequest()}
                        disabled={!isSaveEnabled || isSaved}
                    />
                </MobileButtons>
            </ResponsiveHeader>

            <MainContainer showLoader={false}>
                <Notification scopeToShow="mepNotification" />
                <h2>{title}</h2>
                <I18n component="h3" id="comex.tax.importPayment.label" />
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={() => handleSubmit()}
                    ref={formRef}>
                    {({ values, isSubmitting }) => {
                        setFormData(values);
                        return (
                            <Form>
                                <GridTable className="form">
                                    <GridTable.Container columns={12} columnsTemplate="fixed">
                                        <GridTable.Data columnStart={2} columnWidth={4} rowStart={1}>
                                            <Field
                                                component={DateField}
                                                idForm={FORM_ID}
                                                name="customsEntryDate"
                                                placeholder=" "
                                                value={values.customsEntryDate || ""}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={4} rowStart={2}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="agreedPaymentCondition"
                                                placeholder=" "
                                                maxLength={40}
                                                minLength={1}
                                                value={values.agreedPaymentCondition || ""}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={4} rowStart={3}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="grantedBuyCondition"
                                                placeholder=" "
                                                maxLength={40}
                                                minLength={1}
                                                value={values.grantedBuyCondition || ""}
                                            />
                                        </GridTable.Data>

                                        <GridTable.Data columnStart={7} columnWidth={4} rowStart={1}>
                                            <Field
                                                component={DateField}
                                                idForm={FORM_ID}
                                                name="expirationDate"
                                                placeholder=" "
                                                value={values.expirationDate || ""}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={7} columnWidth={4} rowStart={2}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="arancelPosition"
                                                placeholder=" "
                                                maxLength={15}
                                                minLength={1}
                                                value={values.arancelPosition || ""}
                                            />
                                        </GridTable.Data>
                                        {isB05orB12 && (
                                            <GridTable.Data columnStart={7} columnWidth={4} rowStart={3}>
                                                <Field
                                                    component={DateField}
                                                    idForm={FORM_ID}
                                                    name="deliveryEstimatedTime"
                                                    placeholder=" "
                                                    maxLength={15}
                                                    minLength={1}
                                                    value={values.deliveryEstimatedTime || ""}
                                                />
                                            </GridTable.Data>
                                        )}
                                    </GridTable.Container>
                                </GridTable>
                                <Row className="form-actions">
                                    <Col xs={12} md={4} mdOffset={4}>
                                        <Button
                                            type="submit"
                                            className="btn-primary"
                                            label="global.continue"
                                            loading={isSubmitting}
                                            disabled={!isFormComplete || !isValid}
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};

StepImportPayment.propTypes = {
    isDesktop: bool.isRequired,
};

export default StepImportPayment;
